/**
 * Centralized list of API endpoints for the Cyzor Control Experience API.
 */
export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    ME: '/api/auth/me',
    REFRESH: '/api/auth/refresh',
    LOGOUT: '/api/auth/logout',
  },
  WORKSPACE: {
    LIST: '/api/workspaces',
    LIST_ALL: '/api/workspaces/all',
    DETAILS: (id: string | number) => `/api/workspaces/${id}`,
    CREATE: '/api/workspaces',
    UPDATE: (id: string | number) => `/api/workspaces/${id}`,
    DELETE: (id: string | number) => `/api/workspaces/${id}`,
    MEMBERS: (id: string | number) => `/api/workspaces/${id}/members`,
    MEMBERSHIP: (id: string | number) => `/api/workspaces/${id}/membership`,
    ROLES: (id: string | number) => `/api/workspaces/${id}/roles`,
  },
  DASHBOARD: {
    METRICS: '/api/dashboard/metrics',
    ACTIVITY_FEED: '/api/dashboard/activity-feed',
    ALERTS: '/api/dashboard/alerts',
  },
  CUSTOMERS: {
    LIST: '/api/customers',
    DETAILS: (id: string | number) => `/api/customers/${id}`,
    CREATE: '/api/customers',
    UPDATE: (id: string | number) => `/api/customers/${id}`,
    DELETE: (id: string | number) => `/api/customers/${id}`,
    DASHBOARD: (id: string | number) => `/api/customers/${id}/dashboard`,
  },
  PROJECTS: {
    LIST: '/api/projects',
    DETAILS: (id: string | number) => `/api/projects/${id}`,
    CREATE: '/api/projects',
    UPDATE: (id: string | number) => `/api/projects/${id}`,
    DELETE: (id: string | number) => `/api/projects/${id}`,
    DASHBOARD: (id: string | number) => `/api/projects/${id}/dashboard`,
    KANBAN: (id: string | number) => `/api/projects/${id}/kanban`,
    SPRINTS: (id: string | number) => `/api/projects/${id}/sprints`,
    DOCUMENTS: (id: string | number) => `/api/projects/${id}/documents`,
  },
  EMPLOYEES: {
    LIST: '/api/employees',
    DETAILS: (id: string | number) => `/api/employees/${id}`,
    CREATE: '/api/employees',
    UPDATE: (id: string | number) => `/api/employees/${id}`,
  },
  IDEAS: {
    LIST: '/api/ideas',
    DETAILS: (id: string | number) => `/api/ideas/${id}`,
    CREATE: '/api/ideas',
    UPDATE: (id: string | number) => `/api/ideas/${id}`,
    DELETE: (id: string | number) => `/api/ideas/${id}`,
    DASHBOARD: (id: string | number) => `/api/ideas/${id}/dashboard`,
    REACTIONS: (id: string | number) => `/api/ideas/${id}/reactions`,
  },
  PRODUCTS: {
    LIST: '/api/products',
    DETAILS: (id: string | number) => `/api/products/${id}`,
    CREATE: '/api/products',
    UPDATE: (id: string | number) => `/api/products/${id}`,
    DELETE: (id: string | number) => `/api/products/${id}`,
    DETAILS_FULL: (id: string | number) => `/api/products/${id}/details`,
    DOCUMENTS: (id: string | number) => `/api/products/${id}/documents`,
    DOCUMENTS_UPLOAD: (id: string | number) => `/api/products/${id}/documents/upload`,
    DOCUMENT_DELETE: (id: string | number, documentId: string | number) => `/api/products/${id}/documents/${documentId}`,
    GALLERY: (id: string | number) => `/api/products/${id}/gallery`,
    GALLERY_UPLOAD: (id: string | number) => `/api/products/${id}/gallery/upload`,
    GALLERY_DELETE: (id: string | number, imageId: string | number) => `/api/products/${id}/gallery/${imageId}`,
  },
  PRODUCT_VERSIONS: {
    LIST: '/api/product-versions',
    BY_PRODUCT: (productId: string | number) => `/api/product-versions/product/${productId}`,
    DETAILS: (id: string | number) => `/api/product-versions/${id}`,
    CREATE: '/api/product-versions',
    UPDATE: (id: string | number) => `/api/product-versions/${id}`,
    DELETE: (id: string | number) => `/api/product-versions/${id}`,
  },
  ROADMAP_ITEMS: {
    LIST: '/api/roadmap-items',
    BY_PRODUCT: (productId: string | number) => `/api/roadmap-items/product/${productId}`,
    DETAILS: (id: string | number) => `/api/roadmap-items/${id}`,
    CREATE: '/api/roadmap-items',
    UPDATE: (id: string | number) => `/api/roadmap-items/${id}`,
    DELETE: (id: string | number) => `/api/roadmap-items/${id}`,
  },
  DOCUMENTS: {
    LIST: '/api/documents',
    BY_RESOURCE: (resourceType: string, resourceId: string | number) => `/api/documents/resource/${resourceType}/${resourceId}`,
    DETAILS: (id: string | number) => `/api/documents/${id}`,
    CREATE: '/api/documents',
    UPDATE: (id: string | number) => `/api/documents/${id}`,
    DELETE: (id: string | number) => `/api/documents/${id}`,
    UPLOAD: '/api/documents/upload',
  },
  AUDIT_LOGS: {
    LIST: '/api/audit-logs',
  },
  FILES: {
    LIST: '/api/files',
    UPLOAD: '/api/files/upload',
    DETAILS: (id: string | number) => `/api/files/${id}`,
    DOWNLOAD: (id: string | number) => `/api/files/${id}/download`,
    DELETE: (id: string | number) => `/api/files/${id}`,
  },
  COMPANIES: {
    LIST: '/api/companies',
    DETAILS: (id: string | number) => `/api/companies/${id}`,
    MODULES: (id: string | number) => `/api/companies/${id}/modules`,
  },
  // NOTE: /api/companies maps to Customer (CRM). For tenant/organization management use /api/organizations or /api/admin/companies.
  FINANCIAL: {
    TRANSACTIONS: '/api/financial/transactions',
    REPORTS: '/api/financial/reports',
    CASH_FLOW: '/api/financial/cash-flow',
  },
  CRM: {
    LEADS: '/api/crm/leads',
    DEALS: '/api/crm/deals',
    FUNNEL: '/api/crm/funnel',
  },
  NOTIFICATIONS: {
    LIST: '/api/notifications',
    MARK_READ: (id: string | number) => `/api/notifications/${id}/read`,
    MARK_ALL_READ: '/api/notifications/read-all',
  },
  ANALYTICS: {
    INTELLIGENCE: '/api/analytics/intelligence',
    PREDICTIONS: '/api/analytics/predictions',
  },
  PULSE: {
    FEED: '/api/pulse/feed',
    POSTS: '/api/pulse/posts',
    POST_BY_ID: (id: string | number) => `/api/pulse/posts/${id}`,
    COMMENTS: (postId: string | number) => `/api/pulse/posts/${postId}/comments`,
    REACTIONS: (postId: string | number) => `/api/pulse/posts/${postId}/reactions`,
  },
} as const;